import { Fragment } from 'react';
import { useWatch } from 'react-hook-form';
import type { DimensionValue } from 'v5/core/entity-values/values.types';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponentProps } from './fieldComponent.types';
import { LegacyMeasurementInput } from './LegacyMeasurementInput';

const AXES_2D = ['x', 'y'] as const;
const AXES_3D = ['x', 'y', 'z'] as const;

/**
 * DIMENSION_2D / DIMENSION_3D custom field (legacy `Dimension2DInput` /
 * `Dimension3DInput`): two or three `{ amount, unit }` inputs separated by
 * "by". Each axis validates on its own, so `required` reports the axis.
 */
export function DimensionField({
  spec,
  path,
  hideLabel
}: FieldComponentProps<'dimension'>) {
  const ctx = useFieldContext();
  const axes = spec.axes === 3 ? AXES_3D : AXES_2D;
  const dimension: DimensionValue | undefined = useWatch({ name: path });
  // Units alone are no value (the codec's `isEmpty` agrees).
  const empty = axes.every(
    axis => (dimension?.[axis]?.amount ?? null) === null
  );

  if (ctx.mode === 'display' && empty) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FieldControlBase spec={spec} hideLabel={hideLabel} path={path}>
      <div className="flex flex-row items-start gap-2">
        {axes.map((axis, index) => (
          <Fragment key={axis}>
            {index > 0 && (
              <span
                className="text-fg-muted self-center shrink-0"
                aria-hidden="true"
              >
                by
              </span>
            )}
            <LegacyMeasurementInput
              className="flex-1"
              path={`${path}.${axis}`}
              label={`${spec.label} ${axis.toUpperCase()}`}
              required={spec.required}
              units={spec.units}
              placeholder="Enter value"
            />
          </Fragment>
        ))}
      </div>
    </FieldControlBase>
  );
}
